import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Slider from '@mui/material/Slider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import {
  Settings,
  Image,
  History,
  Save,
  AutoFixHigh as Wand2,
  Upload,
  Palette,
  ZoomOut as Maximize,
  Brush as Eraser,
  Close as X,
  Add as Plus,
  CalendarMonth as Calendar,
  Psychology as Brain,
  AutoAwesome as Sparkles,
  PhotoCamera as Camera
} from '@mui/icons-material';

const ImageGenerationUI = () => {
  const [mode, setMode] = useState('txt2img');
  const [selectedPromptCard, setSelectedPromptCard] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [generateCount, setGenerateCount] = useState(1);
  const [upscaleMode, setUpscaleMode] = useState('STD');
  const [upscaleScale, setUpscaleScale] = useState(2);
  const [upscaleStyle, setUpscaleStyle] = useState('illustration');
  const [upscalePoints, setUpscalePoints] = useState(0);
  const [is4x, setIs4x] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const basePoints = Math.ceil(upscaleScale * upscaleScale * 100);
    let multiplier = 1;
    switch (upscaleMode) {
      case 'STD': multiplier = 0.9; break;
      case 'PRE': multiplier = 0.5; break;
      case 'PLUS': multiplier = 0.1; break;
    }
    setUpscalePoints(Math.ceil(basePoints * multiplier));
  }, [upscaleScale, upscaleMode]);

  const modes = [
    { id: 'txt2img', icon: <Image fontSize="small" />, label: 'Text to Image' },
    { id: 'img2img', icon: <Upload fontSize="small" />, label: 'Image to Image' },
    { id: 'colorize', icon: <Palette fontSize="small" />, label: 'Colorize' },
    { id: 'upscale', icon: <Maximize fontSize="small" />, label: 'Upscale' },
    { id: 'inpaint', icon: <Eraser fontSize="small" />, label: 'Inpaint' },
  ];

  const QuickActions = () => (
    <Stack direction="row" spacing={2} alignItems="center">
      <Button
        variant="contained"
        color="primary"
        startIcon={<Calendar />}
        size="small"
        sx={{ bgcolor: 'primary.light' }}
      >
        予約生成
      </Button>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<Brain />}
        size="small"
        sx={{ bgcolor: 'secondary.light' }}
      >
        AIに相談
      </Button>
      <Button
        variant="contained"
        color="success"
        startIcon={<Sparkles />}
        size="small"
        sx={{ bgcolor: 'success.light' }}
      >
        ランダム
      </Button>
      <FormControlLabel
        control={
          <Checkbox
            checked={is4x}
            onChange={(e) => setIs4x(e.target.checked)}
            size="small"
          />
        }
        label="画素数4倍"
      />
      <Select
        value={generateCount}
        onChange={(e) => setGenerateCount(e.target.value)}
        size="small"
        sx={{ minWidth: 120 }}
      >
        <MenuItem value={1}>1枚生成</MenuItem>
        <MenuItem value={4}>4枚生成</MenuItem>
      </Select>
    </Stack>
  );

  const ModelSelector = () => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography>illustrious_PencilXL-v2.0.0</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" color="primary" size="small">
            生成例
          </Button>
          <Button variant="contained" color="success" size="small">
            推奨設定
          </Button>
        </Stack>
      </Box>
    </Paper>
  );

  const UpscaleSettings = () => (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          倍率
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Slider
            value={upscaleScale}
            onChange={(_, value) => setUpscaleScale(value)}
            min={1}
            max={4}
            step={0.1}
            sx={{ flexGrow: 1 }}
          />
          <Typography sx={{ minWidth: '4rem', textAlign: 'right' }}>
            {upscaleScale}倍
          </Typography>
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          画風
        </Typography>
        <ToggleButtonGroup
          value={upscaleStyle}
          exclusive
          onChange={(_, value) => value && setUpscaleStyle(value)}
        >
          <ToggleButton value="illustration">
            <Palette sx={{ mr: 1 }} />
            イラスト
          </ToggleButton>
          <ToggleButton value="photo">
            <Camera sx={{ mr: 1 }} />
            写真
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>
          品質設定
        </Typography>
        <Grid container spacing={2}>
          {[
            { id: 'STD', label: 'スタンダード', discount: '10%引き' },
            { id: 'PRE', label: 'プレミアム', discount: '50%引き' },
            { id: 'PLUS', label: 'プレミアムプラス', discount: '90%引き' }
          ].map((option) => (
            <Grid item xs={4} key={option.id}>
              <Button
                variant={upscaleMode === option.id ? 'contained' : 'outlined'}
                onClick={() => setUpscaleMode(option.id)}
                fullWidth
                sx={{ height: '100%', flexDirection: 'column' }}
              >
                <Typography variant="body1">{option.label}</Typography>
                <Typography variant="caption">{option.discount}</Typography>
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Typography variant="h6" align="right">
        消費ポイント: {upscalePoints} pt
      </Typography>
    </Stack>
  );

  const QuickGenerate = () => (
    <Box sx={{ py: 2 }}>
      <Grid container spacing={2}>
        {['キャラクター', '風景', '動物', 'その他'].map((category) => (
          <Grid item xs={3} key={category}>
            <Button
              variant="outlined"
              fullWidth
              sx={{ textTransform: 'none' }}
            >
              {category}
            </Button>
          </Grid>
        ))}
      </Grid>
      
      <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Grid container spacing={2}>
          {['髪型', '服装', '表情', 'ポーズ'].map((subCategory) => (
            <Grid item xs={3} key={subCategory}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ textTransform: 'none' }}
              >
                {subCategory}
              </Button>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 'lg', mx: 'auto', p: 4 }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <QuickActions />
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          action={
            <Button
              variant="contained"
              color="warning"
              startIcon={<Wand2 />}
            >
              Generate
            </Button>
          }
          title={
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, value) => value && setMode(value)}
              size="small"
            >
              {modes.map((m) => (
                <ToggleButton key={m.id} value={m.id}>
                  {m.icon}
                  <Box sx={{ ml: 1 }}>{m.label}</Box>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          }
        />

        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={(_, value) => setTabValue(value)}
            >
              <Tab label="Basic" />
              <Tab label="簡単生成" />
              <Tab label="Advanced" />
              <Tab label="Prompt Cards" />
            </Tabs>
          </Box>

          {tabValue === 0 && (
            <Stack spacing={3}>
              <ModelSelector />
              
              {selectedPromptCard && (
                <Paper
                  sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'primary.light'
                  }}
                >
                  <Box
                    component="img"
                    src={selectedPromptCard.thumbnail}
                    sx={{ width: 32, height: 32, borderRadius: 1, mr: 1 }}
                  />
                  <Typography variant="body2">{selectedPromptCard.name}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setSelectedPromptCard(null)}
                    sx={{ ml: 'auto' }}
                  >
                    <X />
                  </IconButton>
                </Paper>
              )}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => setSelectedTags(tags => tags.filter(t => t !== tag))}
                    size="small"
                  />
                ))}
              </Box>

              {mode === 'upscale' ? (
                <UpscaleSettings />
              ) : (
                <>
                  <TextField
                    label="Prompt"
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Enter your prompt here..."
                  />

                  {['img2img', 'colorize', 'upscale', 'inpaint'].includes(mode) && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 4,
                        textAlign: 'center',
                        border: '2px dashed',
                        borderColor: 'divider'
                      }}
                    >
                      <Upload sx={{ mb: 1 }} />
                      <Typography>Drop your image here or click to upload</Typography>
                    </Paper>
                  )}
                </>
              )}
            </Stack>
          )}

          {tabValue === 1 && <QuickGenerate />}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ImageGenerationUI;